import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'react-proptypes';
import axios from 'axios';
import { ButtonComponent, LoaderComponent } from '@dnhb/node_package';
import { BUSY, dispatchMapper, RESET, SYNC } from '../../../shared/wizard/actions';
import { shapeInput, shapeOutput, shapeSettings } from '../config';
import { startAdvanced } from '../helpers';
import getPDF from '../../../shared/helpers/getPdf';
import LeadFormComponent from './LeadFormComponent';
import Card from './Card';
import Analytics from 'shared/utility/analytics';
import HnsLogoSvg from "images/hns/logo.svg";
import { useLocation, useNavigate } from 'react-router-dom';

const RESULTS = 'RESULTS';
const APPOINTMENT = 'APPOINTMENT';
const SENT = 'SENT';

const Header = ({ pages, dispatch, output, settings }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { score } = output;
  const { page } = location.state;

  const getHeaderTitle = (score, lastPage) => {
    if (score <= 4) {
      return 'Advies is noodzakelijk!';
    } else if (score <= 6) {
      return 'Aandacht is vereist';
    } else if (score <= 7) {
      return 'Verbetering is mogelijk';
    }

    return lastPage ? 'Het ziet er goed uit!' : 'U zit mogelijk nog goed';
  }
  
  if (page === 'results') {
    return [
      <p key="title" className="basic">
        {getHeaderTitle(score, false)}
      </p>,
      settings.enablePremiumCalculation ? (
        <p key="sub" className="basic-sub">
          <span role="button" onClick={(e) => { e.preventDefault(); startAdvanced(pages, dispatch, location, navigate); }}>
            Klik hier
          </span> om uw hypotheekcijfer te berekenen
        </p>
      ) : null,
    ];
  }

  return (
    <span className="header__final">
      <strong className={`score-${score}`}>
        U scoort een <b>{score}</b>
      </strong>
      <span className="sub">
        {getHeaderTitle(score, true)}
      </span>
    </span>
  );
}

const OutputResults = (props) => {
  const { input, dispatch, sync, settings } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const [loadingPdf, setLoadingPdf] = useState(false);
  const { page } = location.state;
  const { tab } = input;

  useEffect(() => {
    if (!tab) sync('tab', RESULTS);
  }, []);

  const convertDateInState = (input) => {
    return Object.keys(input).reduce((accumulator, key) => {
      let value = input[key];

      // Handle SplitDateInputComponent value
      if (value && typeof value.value !== 'undefined') {
        ({ value } = input[key]);
      }

      accumulator[key] = value;

      return accumulator;
    }, {})
  }

  const sendMail = (e) => {
    e.preventDefault();
    dispatch({ type: BUSY, location, navigate });
    axios({
      method: 'post',
      url: '/hns/lead',
      data: convertDateInState(input),
    }).then((res) => {
      if (res.data && res.data.length === 0) res.data = {};
      dispatch({
        type: SYNC,
        syncing: false,
        recalculate: null,
        payload: res.data,
        location,
        navigate,
      });
      sync('tab', SENT);
      Analytics.send('Verstuur formulier', 'Verstuur');
    }).catch((error) => {
      const { errors } = error.response.data || {};
      dispatch({ type: SYNC, syncing: false, location, navigate, payload: errors ? { errors } : {} });
    });
  }

  return (
    <div className="results">
      <header>
        <Header {...props} />
        <hr />
        <HnsLogoSvg className="logo" />
      </header>
      {(() => {
        switch (tab) {
          case RESULTS:
            return [
              <div key="content" className="result-content">
                <div className="card-container">
                  <Card cardType="loan_to_value" />
                  <Card cardType="applicant_age" />
                </div>
                <div className="card-container">
                  <Card cardType="mortgage_duration" />
                  <Card cardType="percentage_of_max_mortgage" />
                </div>
              </div>,
              <br key="break" />,
              <div key="lead-buttons" className="row lead-buttons input">
                { loadingPdf ?
                  <div className="input group col-md-6 col-xs-12">
                    <span className="rounded secondary short">
                      <LoaderComponent show />
                    </span>
                  </div> : null }
                {!loadingPdf && settings.enablePdf ? (
                  <ButtonComponent
                    value={page === 'results' ? 'Download verkort rapport' : 'Download rapport'}
                    name="download"
                    id="download"
                    label=""
                    showLabel={false}
                    className="group col-md-6 col-xs-12"
                    inputClassNames="rounded secondary"
                    onClickHandler={() => {
                      setLoadingPdf(true);
                      getPDF(
                        `/hns/pdf/${location.state?.page === 'final' ? 'premium' : 'basic'}`,
                        convertDateInState(input),
                        () => {
                          Analytics.send('Download', 'PDF');
                          setLoadingPdf(false);
                        },
                        '/hns/pdf'
                      );
                    }}
                  />
                ) : null}
                { settings.showLeadButton && !settings.leadButtonUrl ?
                  <ButtonComponent
                    value={settings.leadButtonText || ''}
                    name="lead"
                    id="lead"
                    label=""
                    className="group col-xs-12 col-md-6"
                    inputClassNames="rounded"
                    showLabel={false}
                    onClickHandler={() => {
                      sync('tab', APPOINTMENT);
                      Analytics.send('Open formulier', 'Open');
                    }}
                  /> : null
                }
                { settings.showLeadButton && settings.leadButtonUrl ?
                  <div className="group col-xs-12 col-md-6">
                    <a
                      className="input rounded"
                      target="_blank"
                      rel="noopener noreferrer"
                      href={settings.leadButtonUrl}
                      onClick={() => Analytics.send('Open externe pagina', settings.leadButtonUrl)}
                    >
                      {settings.leadButtonText || ''}
                    </a>
                  </div> : null
                }
              </div>,
            ];
          case APPOINTMENT:
            return (
              <LeadFormComponent
                onSubmit={sendMail}
                onAbort={() => sync('tab', RESULTS)}
              />
            );
          case SENT:
            return (
              <div>
                <div className="form-wrapper lead-sent">
                  <h2><strong>Bedankt!</strong> Er wordt zo snel mogelijk contact met u opgenomen</h2>
                  <p>Nieuwe berekening maken? Klik op de onderstaande knop om opnieuw te beginnen</p>
                </div>
                <nav className="input reset-button">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      dispatch({ type: RESET });
                    }}
                    type="button"
                    className="next rounded"
                  >
                    Nieuwe berekening maken
                  </button>
                </nav>
              </div>
            );
          default:
            return null;
        }
      })()}
      <footer className="no-mobile">
        <button onClick={() => sync('show_disclaimer', true)}>Disclaimer</button>
      </footer>
    </div>
  );
}

OutputResults.propTypes = {
  output: shapeOutput.isRequired,
  input: shapeInput.isRequired,
  settings: shapeSettings.isRequired,
  sync: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

OutputResults.defaultProps = {};

export default connect(state => ({
  settings: state.settings,
  syncing: state.syncing,
  output: state.output,
  input: state.input,
  pages: state.pages,
}), dispatchMapper)(OutputResults);
